import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { RadioAsButtons } from 'ui-components/build';
import { Button, ButtonColorSchema } from 'ui-components/build';
import { useHistory, Redirect } from 'react-router-dom';
import { Input } from 'ui-components/build';
import { useAuthentication } from '../context/AuthenticationProvider';
import { QueryStatus, useMutation } from 'react-query';
import { Spinner } from '../components/Spinner';
import { NamedColorsHEX, ScreenSizesPx } from '../util/styles';
import { toast } from 'react-toastify';

const ScWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 10px 0 10px;
  min-height: calc(100vh - 50px);
  @media screen and (min-width: ${ScreenSizesPx.large}px) {
    justify-content: center;
    padding: 0;
  }
`;

const ScQuestion = styled.h4`
  text-align: center;
  margin: 0 0 20px;
  color: ${NamedColorsHEX.DarkSeaTurquoise};
`;

const ScCountryOption = styled(RadioAsButtons)`
  margin-bottom: 60px;
`;

const ScInput = styled(Input)`
  margin-bottom: 40px;
`;

const ScActionButton = styled(Button)`
  text-transform: uppercase;
  width: 210px;
  margin-bottom: 40px;
`;

const ScButtonRetry = styled(Button)`
  color: black;
  text-transform: uppercase;
`;

const ScParagraph = styled.p`
  margin-bottom: 30px;
`;

export const Login = () => {
  const history = useHistory();
  const { getOtpTokenByPhone, loginViaOtp, user } = useAuthentication();

  const [guestCountry, setGuestCountry] = useState<string>('br');
  const [otp, setOtp] = useState<string>('');
  const [secondsToRetry, setSecondsToRetry] = useState<number>(0);
  const [telephoneNumber, setTelephoneNumber] = useState<string>('+55 ');

  const telephoneInputRef = useRef<HTMLInputElement | null>(null);

  const [
    getOtpToken,
    { status: statusOtp, data: otpToken, reset },
  ] = useMutation<string, unknown, string>(getOtpTokenByPhone);
  const [login, { status: statusLogin }] = useMutation(() => {
    return loginViaOtp(otp, otpToken || '');
  });

  const setCountry = (id: string) => {
    setGuestCountry(id);
    setTelephoneNumber(id === 'pt' ? '+351' : '+55');
  };

  useEffect(() => {
    if (!otpToken) return;

    if (secondsToRetry === 0) return;

    const interval = setInterval(() => {
      if (secondsToRetry === 1) {
        clearInterval(interval);
      }
      setSecondsToRetry(secondsToRetry - 1);
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [otpToken, secondsToRetry]);

  if (user) return <Redirect to="/inicio" />;

  const onClickSendPhone = async () => {
    await getOtpToken(telephoneNumber);
    setSecondsToRetry(30);
  };

  const onClickSendOtp = async () => {
    const res = await login();

    if (res) return history.push('/inicio');

    toast.warning('Algo está errado com telefone ou senha que digitou.');
  };

  return (
    <ScWrapper>
      {[statusOtp, statusLogin].includes(QueryStatus.Loading) && <Spinner />}
      {otpToken ? (
        <>
          <ScParagraph>
            Qual a senha que você recebeu no seu celular?
          </ScParagraph>
          <ScInput
            type="number"
            onChange={(e) => setOtp(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === 'Enter') {
                await onClickSendOtp();
              }
            }}
          />
          <ScActionButton
            colorSchema={ButtonColorSchema.primary}
            disabled={otp.length < 6}
            onClick={onClickSendOtp}
          >
            Escolher o Presente
          </ScActionButton>
          {secondsToRetry > 0 ? (
            <ScParagraph>
              Em {secondsToRetry} segundos você vai poder pedir uma nova senha.
            </ScParagraph>
          ) : (
            <ScButtonRetry
              onClick={() => {
                reset();
              }}
            >
              Pedir nova senha
            </ScButtonRetry>
          )}
        </>
      ) : (
        <>
          <ScQuestion className="babys-cursive">Eu sou...</ScQuestion>
          <ScCountryOption
            selected={guestCountry}
            items={[
              { id: 'br', text: 'Do Brasil' },
              { id: 'pt', text: 'De Portugal' },
            ]}
            onSelect={(v) => {
              setCountry(v);
              telephoneInputRef?.current?.focus();
            }}
          />
          <ScQuestion className="babys-cursive">Meu telefone é...</ScQuestion>
          <ScInput
            type="telephone"
            onChange={(e) => setTelephoneNumber(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === 'Enter') {
                await onClickSendPhone();
              }
            }}
            ref={telephoneInputRef}
            value={telephoneNumber}
          />
          <ScActionButton
            onClick={onClickSendPhone}
            colorSchema={ButtonColorSchema.primary}
          >
            Receber a Senha
          </ScActionButton>
        </>
      )}
    </ScWrapper>
  );
};
